import React, { useCallback, useEffect, useState } from 'react';
import { createEpicorJob } from '../../services/epicorApi';
import {
  listInventoryWarehouses,
  setWarehouseEnabled,
  updateWarehouseDetails,
} from '../../services/inventoryApi';
import { PageHeader, Button, Badge } from '../ui';
import { Toggle } from '../../scheduler/components';

const fmt = (n) => {
  const num = Number(n);
  return Number.isFinite(num) ? num.toLocaleString() : (n ?? '—');
};
const fmtTime = (t) => (t ? new Date(t).toLocaleString() : '—');

const WAREHOUSES_SUB =
  "Warehouse master from Epicor (Erp.Warehse). Turn off warehouses that are not in use — disabled warehouses are excluded from the B2B availability API, the inventory summary counts, and the default stock-levels view. Epicor's own inactive flag is shown for reference but doesn't drive exclusion.";

// Manual detail fields, editable inline. The empty string clears a field.
const DETAIL_FIELDS = [
  { name: 'display_name', label: 'Display name (overrides Epicor description)' },
  { name: 'phone', label: 'Phone' },
  { name: 'operating_hours', label: 'Operating hours', placeholder: 'e.g. Mon-Fri 7:30am-4:30pm' },
  { name: 'address_line1', label: 'Address line 1' },
  { name: 'address_line2', label: 'Address line 2' },
  { name: 'city', label: 'City' },
  { name: 'state', label: 'State' },
  { name: 'postcode', label: 'Postcode' },
  { name: 'country', label: 'Country' },
];

const detailFormFrom = (w) =>
  DETAIL_FIELDS.reduce((acc, f) => ({ ...acc, [f.name]: w[f.name] || '' }), {});

const Warehouses = ({ embedded = false }) => {
  const [warehouses, setWarehouses] = useState([]);
  const [error, setError] = useState(null);
  const [busyKey, setBusyKey] = useState(null); // "CO-CODE" of the toggle in flight
  const [runMsg, setRunMsg] = useState(null);
  const [editingKey, setEditingKey] = useState(null); // "CO-CODE" of the open editor
  const [form, setForm] = useState({}); // seeded once when an editor opens
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const rows = await listInventoryWarehouses({ include_counts: true });
      setWarehouses(rows || []);
    } catch (e) {
      // Non-fatal; the table stays stale.
    }
  }, []);

  // The refresh poll pauses while a details editor is open, so an in-flight
  // edit is never clobbered by a background refetch.
  useEffect(() => {
    load();
    if (editingKey) return undefined;
    const id = setInterval(load, 15000);
    return () => clearInterval(id);
  }, [load, editingKey]);

  const toggle = async (w, enabled) => {
    const key = `${w.company}-${w.warehouse_code}`;
    setError(null);
    setBusyKey(key);
    setWarehouses((prev) =>
      prev.map((x) =>
        x.company === w.company && x.warehouse_code === w.warehouse_code
          ? { ...x, is_enabled: enabled }
          : x,
      ),
    );
    try {
      await setWarehouseEnabled(w.company, w.warehouse_code, enabled);
      load();
    } catch (err) {
      setWarehouses((prev) =>
        prev.map((x) =>
          x.company === w.company && x.warehouse_code === w.warehouse_code
            ? { ...x, is_enabled: !enabled }
            : x,
        ),
      );
      setError(
        err.response?.status === 401 || err.response?.status === 503
          ? `Not authorised to change warehouses (${err.response?.data?.detail || 'API key missing or invalid'}). Configure the sync API key and retry.`
          : err.response?.data?.detail || err.message || 'Failed to update warehouse',
      );
    } finally {
      setBusyKey(null);
    }
  };

  const openEditor = (w) => {
    setError(null);
    setEditingKey(`${w.company}-${w.warehouse_code}`);
    setForm(detailFormFrom(w));
  };

  const closeEditor = () => {
    setEditingKey(null);
    setForm({});
  };

  const saveDetails = async (w) => {
    setError(null);
    setSaving(true);
    try {
      // All detail fields are sent so blanked-out inputs clear their columns.
      const fields = DETAIL_FIELDS.reduce(
        (acc, f) => ({ ...acc, [f.name]: form[f.name]?.trim() || null }),
        {},
      );
      await updateWarehouseDetails(w.company, w.warehouse_code, fields);
      await load();
      closeEditor();
    } catch (err) {
      setError(
        err.response?.status === 401 || err.response?.status === 503
          ? `Not authorised to change warehouses (${err.response?.data?.detail || 'API key missing or invalid'}). Configure the sync API key and retry.`
          : err.response?.data?.detail || err.message || 'Failed to update warehouse details',
      );
    } finally {
      setSaving(false);
    }
  };

  const runWarehouseSync = async () => {
    setRunMsg(null);
    try {
      const { job_id } = await createEpicorJob({
        companies: ['AU', 'NZ'],
        stage: 'warehouses',
      });
      setRunMsg({
        tone: 'ok',
        text: `Queued warehouse sync as Epicor job #${job_id}. This table refreshes automatically once it lands; track progress under Jobs / Job History (Epicor tab) or Manual Syncs → Warehouses.`,
      });
    } catch (err) {
      setRunMsg({ tone: 'error', text: err.response?.data?.detail || err.message });
    }
  };

  const inUse = warehouses.filter((w) => w.is_enabled !== false).length;

  return (
    <div className={embedded ? 'inventory-warehouses' : 'sync-page inventory-warehouses'}>
      {embedded ? (
        <div className="value-lookups-header" style={{ marginTop: '1rem' }}>
          <p className="muted" style={{ margin: 0, maxWidth: '60rem' }}>{WAREHOUSES_SUB}</p>
          <Button variant="secondary" onClick={runWarehouseSync}>
            Sync warehouses from Epicor
          </Button>
        </div>
      ) : (
        <PageHeader
          title="Warehouses"
          sub={WAREHOUSES_SUB}
          actions={
            <>
              <Button variant="secondary" to="/inventory">Overview</Button>
              <Button variant="secondary" onClick={runWarehouseSync}>
                Sync warehouses from Epicor
              </Button>
            </>
          }
        />
      )}

      {runMsg && (
        <div className={runMsg.tone === 'ok' ? 'banner info' : 'banner error'} role="alert">
          {runMsg.text}
        </div>
      )}
      {error && <div className="banner error" role="alert">{error}</div>}

      <section className="card">
        <h3 className="section-h">
          All warehouses
          {warehouses.length > 0 && (
            <span className="muted" style={{ fontWeight: 'normal', marginLeft: 8 }}>
              {inUse} of {warehouses.length} in use
            </span>
          )}
        </h3>
        <table>
          <thead>
            <tr>
              <th>Company</th>
              <th>Code</th>
              <th>Description</th>
              <th>Plant</th>
              <th style={{ textAlign: 'right' }}>SKUs tracked</th>
              <th style={{ textAlign: 'right' }}>SKUs in stock</th>
              <th>Epicor status</th>
              <th>Last synced</th>
              <th>In use</th>
              <th aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {!warehouses.length && (
              <tr><td colSpan={10} className="muted">No warehouses synced yet — run a warehouse or inventory sync.</td></tr>
            )}
            {warehouses.map((w) => {
              const key = `${w.company}-${w.warehouse_code}`;
              const enabled = w.is_enabled !== false;
              const isEditing = editingKey === key;
              return (
                <React.Fragment key={key}>
                  <tr className={enabled ? undefined : 'wh-disabled'}>
                    <td><strong>{w.company}</strong></td>
                    <td>{w.warehouse_code}</td>
                    <td>
                      {w.display_name ? (
                        <>
                          <strong>{w.display_name}</strong>
                          {w.description && (
                            <div className="muted" style={{ fontSize: '0.85em' }}>{w.description}</div>
                          )}
                        </>
                      ) : (
                        w.description || '—'
                      )}
                    </td>
                    <td>{w.plant || '—'}</td>
                    <td style={{ textAlign: 'right' }}>{fmt(w.skus_tracked)}</td>
                    <td style={{ textAlign: 'right' }}>{fmt(w.skus_in_stock)}</td>
                    <td>
                      {w.epicor_inactive
                        ? <Badge variant="warning">inactive in Epicor</Badge>
                        : <Badge variant="success">active</Badge>}
                    </td>
                    <td className="muted">{fmtTime(w.synced_at)}</td>
                    <td>
                      <Toggle
                        checked={enabled}
                        disabled={busyKey === key}
                        label={`${w.company} ${w.warehouse_code} in use`}
                        onChange={(next) => toggle(w, next)}
                      />
                      {w.enabled_updated_at && (
                        <div className="muted" style={{ fontSize: '0.78em' }} title={`Changed by ${w.enabled_updated_by || 'unknown'}`}>
                          {fmtTime(w.enabled_updated_at)}
                        </div>
                      )}
                    </td>
                    <td>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => (isEditing ? closeEditor() : openEditor(w))}
                      >
                        {isEditing ? 'Cancel' : 'Edit'}
                      </Button>
                    </td>
                  </tr>
                  {isEditing && (
                    <tr className="wh-details-editor">
                      <td colSpan={10}>
                        <form
                          onSubmit={(e) => {
                            e.preventDefault();
                            saveDetails(w);
                          }}
                        >
                          <div className="form-grid">
                            {DETAIL_FIELDS.map((f) => (
                              <div className="form-group" key={f.name}>
                                <label htmlFor={`wh-${key}-${f.name}`}>{f.label}</label>
                                <input
                                  id={`wh-${key}-${f.name}`}
                                  type="text"
                                  value={form[f.name] || ''}
                                  placeholder={f.placeholder}
                                  onChange={(e) =>
                                    setForm((prev) => ({ ...prev, [f.name]: e.target.value }))
                                  }
                                />
                              </div>
                            ))}
                          </div>
                          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginTop: '0.75rem' }}>
                            <Button type="submit" disabled={saving}>
                              {saving ? 'Saving…' : 'Save details'}
                            </Button>
                            <Button variant="secondary" type="button" onClick={closeEditor} disabled={saving}>
                              Cancel
                            </Button>
                            {w.details_updated_at && (
                              <span className="muted" style={{ fontSize: '0.85em' }}>
                                Last edited {fmtTime(w.details_updated_at)} by {w.details_updated_by || 'unknown'}
                              </span>
                            )}
                          </div>
                        </form>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </section>
    </div>
  );
};

export default Warehouses;
